/* ==================================================================
   GUSTAVARIAN — theme.js
   Everything the React prototype did, in vanilla JS.
   ================================================================== */
(function () {
  'use strict';

  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const G = window.GSTVRN || {};

  /* ---------------------------------------------------------------- *
   *  THEME + LOGO
   *
   *  One owner for the logo src. The last build had the theme toggle
   *  and the hero-film rule both writing it on every scroll, so they
   *  fought and the logo stopped switching. Now `paintLogos()` is the
   *  only writer and it considers both inputs at once.
   * ---------------------------------------------------------------- */
  const hdr = $('[data-header]');
  const overFilm = hdr && hdr.hasAttribute('data-over-film');
  let scrolled = false;

  const mode = () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark');

  function paintLogos() {
    // over the hero the UI is white in both themes, so the light mark wins
    const onFilm = overFilm && !scrolled;
    const src = onFilm || mode() === 'dark' ? G.logoLight : G.logoDark;
    $$('[data-logo]').forEach(img => { if (src && img.src !== src) img.src = src; });
  }

  function setTheme(next) {
    const h = document.documentElement;
    h.dataset.theme = next;
    h.style.colorScheme = next;
    try { localStorage.setItem('gstvrn-theme', next); } catch (e) {}
    $('meta[name="theme-color"]')?.setAttribute('content', next === 'dark' ? '#050506' : '#f6f6f7');
    paintLogos();
    syncToggles();
    dispatchEvent(new CustomEvent('gstvrn:theme'));
  }

  function syncToggles() {
    const dark = mode() === 'dark';
    $$('[data-theme-toggle]').forEach(b => {
      b.setAttribute('aria-label', `Switch to ${dark ? 'light' : 'dark'} mode`);
      $('[data-icon-sun]', b)?.toggleAttribute('hidden', !dark);
      $('[data-icon-moon]', b)?.toggleAttribute('hidden', dark);
    });
  }

  $$('[data-theme-toggle]').forEach(b =>
    b.addEventListener('click', () => setTheme(mode() === 'dark' ? 'light' : 'dark')));
  syncToggles();
  paintLogos();

  /* camo — a dark-mode treatment; the control says so rather than
     silently doing nothing in light mode */
  function syncCamo() {
    const on = document.documentElement.classList.contains('has-camo');
    const dark = mode() === 'dark';
    $$('[data-camo-toggle]').forEach(b => {
      b.textContent = dark ? `Camo ${on ? 'on' : 'off'}` : 'Camo — dark only';
      b.disabled = !dark;
      b.setAttribute('aria-pressed', String(on));
    });
  }
  $$('[data-camo-toggle]').forEach(b => b.addEventListener('click', () => {
    const on = document.documentElement.classList.toggle('has-camo');
    try { localStorage.setItem('gstvrn-camo', on ? '1' : '0'); } catch (e) {}
    syncCamo();
  }));
  addEventListener('gstvrn:theme', syncCamo);
  syncCamo();

  /* ---------------------------------------------------------------- *
   *  HEADER state
   * ---------------------------------------------------------------- */
  if (hdr) {
    const onScroll = () => {
      const next = scrollY > 24;
      if (next === scrolled) return;
      scrolled = next;
      const solid = scrolled || !overFilm;
      hdr.classList.toggle('hdr-film', !solid);
      hdr.classList.toggle('border-edge', solid);
      hdr.classList.toggle('bg-void/80', solid);
      hdr.classList.toggle('backdrop-blur-xl', solid);
      hdr.classList.toggle('border-transparent', !solid);
      paintLogos();
    };
    scrolled = scrollY > 24;
    // seed the opposite so the first call always applies
    scrolled = !scrolled; onScroll();
    addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------- *
   *  REVEAL on scroll
   * ---------------------------------------------------------------- */
  if (!reduced && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(es => {
      es.forEach(e => {
        if (!e.isIntersecting) return;
        const d = e.target.dataset.revealDelay;
        if (d) e.target.style.transitionDelay = `${d}ms`;
        e.target.classList.add('in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
    $$('[data-reveal]').forEach(el => io.observe(el));
  } else {
    $$('[data-reveal]').forEach(el => el.classList.add('in'));
  }

  /* ---------------------------------------------------------------- *
   *  HERO — parallax drift + reel switching
   * ---------------------------------------------------------------- */
  const hero = $('[data-hero]');
  if (hero && !reduced) {
    const media = $('[data-hero-media]', hero);
    let ticking = false;
    addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const p = Math.min(Math.max(scrollY / hero.offsetHeight, 0), 1);
        media.style.transform = `translateY(${p * 16}%) scale(${1 + p * 0.1})`;
        media.style.opacity = String(1 - p * 1.25);
        ticking = false;
      });
    }, { passive: true });
  }

  $$('[data-reel]').forEach(btn => btn.addEventListener('click', () => {
    const v = $('[data-hero-video]');
    if (!v) return;
    $$('[data-reel]').forEach(b => {
      const on = b === btn;
      b.setAttribute('aria-pressed', String(on));
      b.classList.toggle('border-white', on);
      b.classList.toggle('bg-white', on);
      b.classList.toggle('text-[#07070a]', on);
      b.classList.toggle('border-white/20', !on);
      b.classList.toggle('text-white/45', !on);
    });
    v.src = btn.dataset.src;
    v.poster = btn.dataset.poster;
    v.play().catch(() => {});
    const meta = $('[data-hero-meta]');
    if (meta && btn.dataset.meta) scramble(meta, btn.dataset.meta);
  }));

  $$('video[autoplay]').forEach(v => v.play().catch(() => {
    const kick = () => { v.play().catch(() => {}); removeEventListener('pointerdown', kick); };
    addEventListener('pointerdown', kick, { once: true });
  }));

  /* ---------------------------------------------------------------- *
   *  SCRAMBLE — text resolves out of static
   * ---------------------------------------------------------------- */
  const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\<>[]{}=+*#%$@';
  function scramble(el, text) {
    text = text != null ? text : (el.dataset.scramble || el.textContent);
    if (reduced) { el.textContent = text; return; }
    const chars = text.split('');
    let frame = 0;
    (function tick() {
      const settled = Math.floor(frame / 2.6);
      el.textContent = chars
        .map((c, i) => (c === ' ' ? ' ' : i < settled ? c : GLYPHS[(Math.random() * GLYPHS.length) | 0]))
        .join('');
      if (settled >= chars.length) { el.textContent = text; return; }
      frame++;
      requestAnimationFrame(tick);
    })();
  }
  $$('[data-scramble]').forEach(el =>
    setTimeout(() => scramble(el), Number(el.dataset.scrambleDelay || 0)));

  /* ---------------------------------------------------------------- *
   *  SHEETS — open/close, Escape, focus trap, drag-to-dismiss
   * ---------------------------------------------------------------- */
  let lastFocus = null;

  const openSheet = s => {
    lastFocus = document.activeElement;
    s.classList.add('is-open');
    s.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => $('button, a, input', s)?.focus(), 80);
  };
  const closeSheet = s => {
    s.classList.remove('is-open');
    s.setAttribute('aria-hidden', 'true');
    if (!$('.sheet.is-open')) document.body.style.overflow = '';
    lastFocus?.focus?.();
  };
  window.gstvrnOpenSheet = id => { const s = document.getElementById(id); if (s) openSheet(s); };

  document.addEventListener('click', e => {
    const o = e.target.closest('[data-sheet-open]');
    if (o) { e.preventDefault(); const s = document.getElementById(o.dataset.sheetOpen); if (s) openSheet(s); return; }
    if (e.target.closest('[data-sheet-close]') || e.target.classList.contains('sheet__scrim')) {
      const s = e.target.closest('.sheet');
      if (s) closeSheet(s);
    }
  });

  addEventListener('keydown', e => {
    const sheet = $('.sheet.is-open');
    if (!sheet) return;
    if (e.key === 'Escape') { closeSheet(sheet); return; }
    if (e.key !== 'Tab') return;
    const f = $$('a[href], button:not([disabled]), input, select, textarea', sheet)
      .filter(el => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  $$('.sheet__grab').forEach(grab => {
    const sheet = grab.closest('.sheet');
    const panel = grab.closest('.sheet__panel');
    let y0 = 0, dy = 0, dragging = false;
    const down = e => { dragging = true; dy = 0; y0 = (e.touches ? e.touches[0] : e).clientY; panel.classList.add('is-dragging'); };
    const move = e => {
      if (!dragging) return;
      dy = Math.max(0, (e.touches ? e.touches[0] : e).clientY - y0);
      panel.style.transform = `translateY(${dy}px)`;
    };
    const up = () => {
      if (!dragging) return;
      dragging = false;
      panel.classList.remove('is-dragging');
      panel.style.transform = '';
      if (dy > 110) closeSheet(sheet);
    };
    grab.addEventListener('pointerdown', down);
    addEventListener('pointermove', move, { passive: true });
    addEventListener('pointerup', up);
    grab.addEventListener('touchstart', down, { passive: true });
    addEventListener('touchmove', move, { passive: true });
    addEventListener('touchend', up);
  });

  /* ---------------------------------------------------------------- *
   *  ACCORDIONS — menu children, FAQ
   * ---------------------------------------------------------------- */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-accordion]');
    if (!btn) return;
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    const open = btn.getAttribute('aria-expanded') === 'true';

    if (!open && btn.dataset.accordionGroup) {
      $$(`[data-accordion-group="${btn.dataset.accordionGroup}"]`).forEach(other => {
        if (other === btn) return;
        other.setAttribute('aria-expanded', 'false');
        $('[data-chev]', other)?.style.setProperty('transform', '');
        const p = document.getElementById(other.getAttribute('aria-controls'));
        if (p) p.style.height = '0px';
      });
    }

    btn.setAttribute('aria-expanded', String(!open));
    // group-aria-expanded isn't a Tailwind variant, so rotate directly
    const chev = $('[data-chev]', btn);
    if (chev) chev.style.transform = open ? '' : (chev.textContent.trim() === '+' ? 'rotate(45deg)' : 'rotate(180deg)');
    panel.style.height = open ? '0px' : `${panel.scrollHeight}px`;
  });

  /* ---------------------------------------------------------------- *
   *  UNIT TOGGLE — size guide tables
   * ---------------------------------------------------------------- */
  $$('[data-unit-toggle]').forEach(group => group.addEventListener('click', e => {
    const btn = e.target.closest('[data-unit]');
    if (!btn) return;
    const unit = btn.dataset.unit;
    $$('[data-unit]', group).forEach(b => {
      const on = b === btn;
      b.setAttribute('aria-pressed', String(on));
      b.classList.toggle('bg-bone', on);
      b.classList.toggle('text-void', on);
      b.classList.toggle('text-ash', !on);
    });
    /* A toggle governs every table it shares a page — or a sheet — with.
       Walking up to the nearest div found the toggle's own wrapper, which
       contains no tables at all, so nothing ever converted. */
    const scope = group.closest('.sheet') || document;
    $$('[data-in]', scope).forEach(td => {
      td.textContent = unit === 'in' ? `${td.dataset.in}"` : `${td.dataset.cm} cm`;
    });
  }));

  /* ---------------------------------------------------------------- *
   *  CART — Shopify AJAX API
   * ---------------------------------------------------------------- */
  const money = cents => {
    const fmt = G.moneyFormat || '£{{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString());
  };
  const esc = s => String(s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const Cart = {
    async get() { return (await fetch('/cart.js')).json(); },
    async add(id, qty) {
      const r = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: qty || 1 }] }),
      });
      if (!r.ok) throw new Error((await r.json()).description || 'Could not add to bag');
      return this.refresh();
    },
    async change(key, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      return this.render(await r.json());
    },
    async refresh() { return this.render(await this.get()); },
    render(cart) {
      $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
      const body = $('[data-cart-body]'), foot = $('[data-cart-foot]');
      if (!body) return cart;

      if (!cart.items.length) {
        body.innerHTML =
          '<div class="py-16 text-center"><p class="text-[14px] text-ash">Your bag is empty.</p>' +
          '<a href="/collections/all" class="pill pill-ghost mt-5" data-sheet-close>Start shopping</a></div>';
        if (foot) foot.hidden = true;
        return cart;
      }
      if (foot) foot.hidden = false;

      body.innerHTML = cart.items.map(i => `
        <div class="grid grid-cols-[64px_1fr_auto] gap-3.5 border-b border-edge py-4">
          <a href="${i.url}" class="block h-20 w-16 overflow-hidden rounded-lg bg-slab">
            <img src="${i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : ''}" alt=""
                 width="64" height="80" loading="lazy" class="h-full w-full object-cover">
          </a>
          <div class="min-w-0">
            <h4 class="text-[14px] font-medium text-bone">${esc(i.product_title)}</h4>
            ${i.variant_title && i.variant_title !== 'Default Title'
              ? `<p class="mt-0.5 font-mono text-[10px] tracking-[0.12em] text-smoke uppercase">${esc(i.variant_title)}</p>` : ''}
            <div class="mt-2 inline-flex items-center rounded-full border border-edge">
              <button type="button" class="h-[30px] w-[30px] text-ash" data-qty="${i.key}" data-to="${i.quantity - 1}" aria-label="Decrease">−</button>
              <span class="min-w-[22px] text-center font-mono text-[11px]">${i.quantity}</span>
              <button type="button" class="h-[30px] w-[30px] text-ash" data-qty="${i.key}" data-to="${i.quantity + 1}" aria-label="Increase">+</button>
            </div>
          </div>
          <div class="font-mono text-[13px]">${money(i.final_line_price)}</div>
        </div>`).join('');

      const sub = $('[data-cart-subtotal]');
      if (sub) sub.textContent = money(cart.total_price);
      return cart;
    },
  };
  window.GSTVRNCart = Cart;

  document.addEventListener('click', async e => {
    const q = e.target.closest('[data-qty]');
    if (q) { await Cart.change(q.dataset.qty, Number(q.dataset.to)); return; }

    const add = e.target.closest('[data-add-to-cart]');
    if (!add) return;
    e.preventDefault();
    const form = add.closest('form');
    const id = form ? $('[name="id"]', form)?.value : add.dataset.variantId;
    if (!id) return;
    const qty = form ? Number($('[name="quantity"]', form)?.value || 1) : 1;
    const label = $('[data-add-label]', add) || add;
    const was = label.textContent;
    add.disabled = true;
    try {
      await Cart.add(id, qty);
      label.textContent = 'Added ✓';
      window.gstvrnOpenSheet('cart-sheet');
    } catch (err) {
      label.textContent = err.message;
    } finally {
      setTimeout(() => { label.textContent = was; add.disabled = false; }, 1800);
    }
  });

  Cart.refresh().catch(() => {});

  /* ---------------------------------------------------------------- *
   *  PRODUCT — variant picker
   * ---------------------------------------------------------------- */
  $$('[data-product]').forEach(root => {
    let variants = [];
    try { variants = JSON.parse($('[data-variants]', root).textContent); } catch (e) { return; }

    const idField = $('[name="id"]', root);
    const priceEl = $('[data-price]', root);
    const wasEl   = $('[data-compare]', root);
    const addBtn  = $('[data-add-to-cart]', root);
    const stockEl = $('[data-stock]', root);
    const groups  = () => $$('[data-option]', root);

    const chosen = () => groups().map(g => $('input:checked', g)?.value ?? null);
    const match  = sel => variants.find(v => v.options.every((o, i) => o === sel[i]));

    function update() {
      groups().forEach(g => $$('label', g).forEach(l => {
        const on = !!$('input', l)?.checked;
        l.classList.toggle('border-bone', on);
        l.classList.toggle('bg-bone', on);
        l.classList.toggle('text-void', on);
        l.classList.toggle('border-edge', !on);
        l.classList.toggle('text-ash', !on);
      }));

      const v = match(chosen());
      const label = $('[data-add-label]', addBtn);
      if (!v) { addBtn.disabled = true; label.textContent = 'Unavailable'; return; }

      idField.value = v.id;
      if (priceEl) priceEl.textContent = money(v.price);
      if (wasEl) {
        const on = v.compare_at_price && v.compare_at_price > v.price;
        wasEl.hidden = !on;
        if (on) wasEl.textContent = money(v.compare_at_price);
      }
      if (stockEl) stockEl.textContent = v.available ? `${v.inventory} left` : 'Sold out';
      addBtn.disabled = !v.available;
      label.textContent = v.available ? 'Add to bag' : 'Sold out';

      if (history.replaceState) {
        const u = new URL(location.href);
        u.searchParams.set('variant', v.id);
        history.replaceState({}, '', u);
      }
    }
    root.addEventListener('change', update);
    update();
  });

  /* PDP mobile gallery dots */
  const rail = $('[data-pdp-rail]');
  if (rail) {
    const dots = $$('[data-pdp-dots] span');
    rail.addEventListener('scroll', () => {
      const i = Math.round(rail.scrollLeft / (rail.clientWidth * 0.86));
      dots.forEach((d, j) => {
        d.classList.toggle('w-5', j === i);
        d.classList.toggle('bg-bone', j === i);
        d.classList.toggle('w-1', j !== i);
        d.classList.toggle('bg-edge', j !== i);
      });
    }, { passive: true });
  }

  /* ---------------------------------------------------------------- *
   *  COOKIE CONSENT
   * ---------------------------------------------------------------- */
  const consent = $('[data-consent]');
  const consentAnswered = () => {
    try { return !!localStorage.getItem('gstvrn-consent'); } catch (e) { return true; }
  };
  if (consent) {
    if (!consentAnswered()) setTimeout(() => consent.classList.add('is-open'), 1400);

    consent.addEventListener('click', e => {
      const choice = e.target.closest('[data-consent-choice]');
      if (choice) {
        try { localStorage.setItem('gstvrn-consent', choice.dataset.consentChoice); } catch (err) {}
        consent.classList.remove('is-open');
        maybeOpenNewsletter();
      }
      const more = e.target.closest('[data-consent-detail]');
      if (more) {
        const panel = $('[data-consent-panel]', consent);
        const open = panel.style.height && panel.style.height !== '0px';
        panel.style.height = open ? '0px' : `${panel.scrollHeight}px`;
        more.textContent = open ? 'What we store' : 'Hide detail';
      }
    });
    $$('[data-consent-reopen]').forEach(b =>
      b.addEventListener('click', () => consent.classList.add('is-open')));
  }

  /* ---------------------------------------------------------------- *
   *  NEWSLETTER POPUP
   *
   *  Queued behind the cookie notice rather than racing it: stacking two
   *  dialogues on first paint buries the one the visitor is owed a choice
   *  about. If consent is already answered it opens on its own timer.
   * ---------------------------------------------------------------- */
  const nlpop = $('#newsletter-popup');

  function maybeOpenNewsletter() {
    if (!nlpop || nlpop.classList.contains('is-open')) return;
    let seen = null;
    try { seen = localStorage.getItem('gstvrn-newsletter'); } catch (e) { seen = 'skip'; }
    if (seen) return;                       // dismissed or subscribed already
    if (!consentAnswered()) return;         // wait for the cookie choice
    const wait = parseInt(nlpop.dataset.delay || '900', 10);
    setTimeout(() => {
      if (nlpop.classList.contains('is-open')) return;
      nlpop.classList.add('is-open');
      nlpop.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => $('input[type=email]', nlpop)?.focus(), 120);
    }, wait);
  }

  function closeNewsletter(remember) {
    if (!nlpop) return;
    nlpop.classList.remove('is-open');
    nlpop.setAttribute('aria-hidden', 'true');
    if (!$('.sheet.is-open')) document.body.style.overflow = '';
    if (remember) {
      try { localStorage.setItem('gstvrn-newsletter', 'closed'); } catch (e) {}
    }
  }

  if (nlpop) {
    /* a successful signup re-renders the form server-side — never ask again */
    if ($('[name="contact[email]"]', nlpop) === null) {
      try { localStorage.setItem('gstvrn-newsletter', 'subscribed'); } catch (e) {}
    }
    nlpop.addEventListener('click', e => {
      if (e.target.closest('[data-nlpop-close]')) closeNewsletter(true);
    });
    addEventListener('keydown', e => {
      if (e.key === 'Escape' && nlpop.classList.contains('is-open')) closeNewsletter(true);
    });
    $('form', nlpop)?.addEventListener('submit', () => {
      try { localStorage.setItem('gstvrn-newsletter', 'subscribed'); } catch (e) {}
    });
    maybeOpenNewsletter();
  }

  /* ---------------------------------------------------------------- *
   *  NAVIGATION POLISH — buy back some of the SPA feel
   * ---------------------------------------------------------------- */
  if ('startViewTransition' in document && !reduced) {
    document.addEventListener('click', e => {
      const a = e.target.closest('a[href]');
      if (!a || a.target || a.hasAttribute('download') || a.hasAttribute('data-no-transition')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || url.pathname === location.pathname) return;
      e.preventDefault();
      document.startViewTransition(() => { location.href = a.href; });
    });
  }

  const seen = new Set();
  document.addEventListener('pointerover', e => {
    const a = e.target.closest('a[href]');
    if (!a || a.target) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || seen.has(url.pathname)) return;
    seen.add(url.pathname);
    const l = document.createElement('link');
    l.rel = 'prefetch'; l.href = url.pathname + url.search;
    document.head.appendChild(l);
  }, { passive: true });
})();
